/**
 * orderResume.ts — the ONE definition of "this order can be picked back up".
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: order-history resume
 * (CTO ask, 2026-07-30).
 *
 * WHY: both order-history surfaces (the dashboard OrdersCard and the My orders
 * section) offer "Continue checkout" on the same rule — a plan chosen but
 * payment never started, or a checkout that stopped at the payment step. Both
 * resume through the existing signed-in `#resume=<assessmentRef>` entry (the
 * same one the admin plan-link handover uses) — CheckoutFlow's authed
 * `checkout/resume` read restores the exact step. One shared helper so the two
 * surfaces cannot drift (review of PR #134).
 *
 * FRAGMENT, not query: an assessment id in `?resume=` lands in access logs,
 * browser history and GTM's `page_location`. Nothing we mint may use the query
 * form — `journey/resumeHandoff.ts` only reads it to keep already-emailed links
 * working, and scrubs it on arrival.
 */
import type { OrderSummary } from '../api/types';

const RESUMABLE_STATES = new Set(['plan_selected', 'awaiting_payment']);

/** The `/weightloss#resume=` href for a resumable order, or null when the row
 * is not resumable (wrong state, or the dashboard read predates the orders
 * `assessmentRef` widening — the row then renders plain, no dead button). */
export function resumeHref(order: OrderSummary): string | null {
  if (!RESUMABLE_STATES.has(order.state) || !order.assessmentRef) return null;
  return `/weightloss#resume=${encodeURIComponent(order.assessmentRef)}`;
}
